import { overlaps, type Vector } from './overlaps.utils';

// WARNING: This function was largely written by an LLM
export function generateClusterCenters(clusters: number, canvas: number, radius: number, minDist: number): Vector[] {
  const centers: Vector[] = [];

  let attempts = 0;
  while (centers.length < clusters && attempts < clusters * 300) {
    const cx = radius + Math.floor(Math.random() * (canvas - radius * 2));
    const cy = radius + Math.floor(Math.random() * (canvas - radius * 2));

    if (!centers.some((c) => Math.hypot(cx - c.x, cy - c.y) < minDist)) {
      centers.push({ x: cx, y: cy });
    }

    attempts++;
  }

  return centers;
}

// WARNING: This function was largely written by an LLM
export function placeClusterGroup(
  center: Vector,
  count: number,
  radius: number,
  placed: Vector[],
  dot: number,
  max: number,
  maxAttempts: number,
): Vector[] {
  const newPlacements: Vector[] = [];

  let attempts = 0;
  while (newPlacements.length < count && attempts < maxAttempts) {
    // From the centre, generate a random angle and distance to place the point.
    const angle = Math.random() * Math.PI * 2;
    const r = Math.random() * radius;
    const x = Math.min(max, Math.max(0, Math.floor(center.x + Math.cos(angle) * r)));
    const y = Math.min(max, Math.max(0, Math.floor(center.y + Math.sin(angle) * r)));

    if (!overlaps(placed, dot, x, y) && !overlaps(newPlacements, dot, x, y)) {
      newPlacements.push({ x, y });
    }

    attempts++;
  }

  return newPlacements;
}

// WARNING: This function was largely written by an LLM
export function placeSpread(count: number, max: number, minDist: number, maxAttempts: number): Vector[] {
  const placed: Vector[] = [];

  let attempts = 0;
  while (placed.length < count && attempts < maxAttempts) {
    const x = Math.floor(Math.random() * (max + 1));
    const y = Math.floor(Math.random() * (max + 1));

    if (!overlaps(placed, minDist, x, y)) {
      placed.push({ x, y });
    }

    attempts++;
  }

  return placed;
}

// WARNING: This function was largely written by an LLM
export function distribute(canvasSize = 100, dotSize = 5, count: number, clusters?: number): Vector[] {
  const MAX = canvasSize - dotSize;
  const MAX_ATTEMPTS = count * 100;
  const placed: Vector[] = [];

  if (clusters && clusters > 1) {
    const CLUSTER_RADIUS = 12;
    const MIN_CENTER_DIST = CLUSTER_RADIUS * 2.5;
    const centers = generateClusterCenters(clusters, canvasSize, CLUSTER_RADIUS, MIN_CENTER_DIST);
    const groupSize = Math.floor(count / clusters);
    const remainder = count % clusters;

    for (let g = 0; g < centers.length; g++) {
      const groupCount = groupSize + (g < remainder ? 1 : 0);
      placed.push(...placeClusterGroup(centers[g], groupCount, CLUSTER_RADIUS, placed, dotSize, MAX, MAX_ATTEMPTS));
    }
  } else {
    const MIN_SPREAD = dotSize * 4; // enforces spacing, tweak as needed
    placed.push(...placeSpread(count, MAX, MIN_SPREAD, MAX_ATTEMPTS));
  }

  if (placed.length < count) {
    throw new Error(`Only placed ${placed.length}/${count} dots — canvas too full.`);
  }

  return placed;
}
